import { useState } from 'react';
import { X, UserPlus, UserMinus, Lock } from 'lucide-react';
import {
  useDossierChannelMembers,
  useInviteManualMember,
  useRemoveManualMember,
  useCollaboratorsForMessaging,
} from '../../hooks/queries/useMessaging';
import { useAuthStore } from '../../store/useAuthStore';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import { CollaboratorSelect } from '../collaborators/CollaboratorSelect';

interface Props {
  channelId: string;
  onClose: () => void;
}

export function DossierChannelMembersModal({ channelId, onClose }: Props) {
  const [selectedId, setSelectedId] = useState('');
  const collaborator = useAuthStore((s) => s.collaborator);

  const { data: members = [], isLoading } = useDossierChannelMembers(channelId);
  const { data: allCollaborators = [] } = useCollaboratorsForMessaging();
  const invite = useInviteManualMember();
  const remove = useRemoveManualMember();

  const memberIds = new Set(members.map((m) => m.collaboratorId));
  if (collaborator?.id) memberIds.add(collaborator.id);
  const available = allCollaborators.filter((c) => !memberIds.has(c.id));

  const intervenants = members.filter((m) => m.source === 'auto');
  const invites = members.filter((m) => m.source === 'manual');

  function handleAdd() {
    if (!selectedId) return;
    invite.mutate(
      { channelId, collaboratorId: selectedId },
      { onSuccess: () => setSelectedId('') },
    );
  }

  function handleRemove(collaboratorId: string) {
    remove.mutate({ channelId, collaboratorId });
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-cime-surface rounded-xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h2 className="font-heading text-lg font-semibold text-cime-text-primary">
            Membres du salon
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700 transition"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-5">
          {isLoading ? (
            <p className="text-sm text-cime-text-muted">Chargement…</p>
          ) : (
            <>
              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-cime-text-muted mb-2">
                  Intervenants du dossier
                </h3>
                {intervenants.length === 0 ? (
                  <p className="text-xs text-cime-text-muted italic">Aucun intervenant.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {intervenants.map((m) => (
                      <li
                        key={m.collaboratorId}
                        className="flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-neutral-50"
                      >
                        <CollaboratorLink collaboratorId={m.collaboratorId} title={`Voir le profil de ${m.fullName}`}>
                          <span className="text-cime-text-primary">{m.fullName}</span>
                        </CollaboratorLink>
                        <span className="inline-flex items-center gap-1 text-xs text-cime-text-muted">
                          <Lock className="w-3 h-3" />
                          Auto
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[11px] text-cime-text-muted mt-2">
                  Synchronisés avec l'onglet « Équipe » du dossier. Retirez-les depuis l'équipe pour
                  les sortir du salon.
                </p>
              </section>

              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-cime-text-muted mb-2">
                  Invités
                </h3>
                {invites.length === 0 ? (
                  <p className="text-xs text-cime-text-muted italic">Aucun invité.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {invites.map((m) => (
                      <li
                        key={m.collaboratorId}
                        className="flex items-center justify-between text-sm py-1.5 px-2 rounded hover:bg-neutral-50"
                      >
                        <CollaboratorLink collaboratorId={m.collaboratorId} title={`Voir le profil de ${m.fullName}`}>
                          <span className="text-cime-text-primary">{m.fullName}</span>
                        </CollaboratorLink>
                        <button
                          type="button"
                          onClick={() => handleRemove(m.collaboratorId)}
                          disabled={remove.isPending}
                          className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 disabled:opacity-40"
                        >
                          <UserMinus className="w-3.5 h-3.5" />
                          Retirer
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-cime-text-muted mb-2">
                  Inviter un collaborateur
                </h3>
                {available.length === 0 ? (
                  <p className="text-xs text-cime-text-muted italic">
                    Tous les collaborateurs sont déjà membres.
                  </p>
                ) : (
                  <div className="flex gap-2">
                    <div className="flex-1 min-w-0">
                      <CollaboratorSelect
                        options={available}
                        value={selectedId}
                        onChange={setSelectedId}
                        placeholder="— Choisir —"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleAdd}
                      disabled={!selectedId || invite.isPending}
                      className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md bg-cime-primary text-white hover:opacity-90 disabled:opacity-40 transition"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      Inviter
                    </button>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
